import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { User, UserFormData } from "./user";
import { USE_STUB_DATA, STUB_USERS } from "../../config/stubData";
import { RootState } from "../../store";
import { userService } from "./userService";
import toast from "react-hot-toast";
import { DEFAULT_PAGINATION } from "../../constants/pagination";


interface Pagination {
  page: number;
  limit: number;
  pages: number;
  total: number;
}

interface Counts {
  total: number;
  active: number;
  inactive: number;
}

interface UserState {
  users: User[];
  loading: boolean;
  error: string | null;
  selectedUser: User | null;
  pagination: Pagination;
  counts: Counts;
}

const initialState: UserState = {
  users: USE_STUB_DATA ? STUB_USERS : [],
  loading: false,
  error: null,
  selectedUser: null,
  pagination: {
    page: DEFAULT_PAGINATION.page,
    limit: DEFAULT_PAGINATION.limit,
    pages: 1,
    total: USE_STUB_DATA ? STUB_USERS.length : 0,
  },
  counts: {
    total: 0,
    active: 0,
    inactive: 0,
  }
};

let nextStubId = STUB_USERS.length + 1;

// Async thunks
export const fetchUsers = createAsyncThunk<
  { users: User[]; pagination: Pagination; counts: Counts },
  { page?: number; limit?: number; search?: string; role?: string; status?: string; branchId?: string },
  { rejectValue: string }
>("users/fetchUsers", async (params = {}, { rejectWithValue }) => {
  if (USE_STUB_DATA) {
    const { 
      page = DEFAULT_PAGINATION.page, 
      limit = DEFAULT_PAGINATION.limit, 
      search = "", 
      role = "", 
      status = "", 
      branchId = "" 
    } = params;
    
    // Apply filters to stub data
    let filteredUsers = STUB_USERS;
    
    if (search) {
      filteredUsers = filteredUsers.filter(user => 
        user.firstName.toLowerCase().includes(search.toLowerCase()) ||
        user.lastName.toLowerCase().includes(search.toLowerCase()) ||
        user.email.toLowerCase().includes(search.toLowerCase()) ||
        user.role.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    if (role) {
      filteredUsers = filteredUsers.filter(user => user.role === role);
    }
    
    if (status !== "") {
      const isActive = status === "active";
      filteredUsers = filteredUsers.filter(user => user.status === isActive);
    }
    
    if (branchId) {
      filteredUsers = filteredUsers.filter(user => user.branchId === branchId);
    }
    
    // Apply pagination
    const total = filteredUsers.length;
    const pages = Math.ceil(total / limit);
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
    
    return {
      users: paginatedUsers,
      pagination: {
        page,
        limit,
        pages,
        total,
      },
      counts: {
        total: STUB_USERS.length,
        active: STUB_USERS.filter(user => user.status).length,
        inactive: STUB_USERS.filter(user => !user.status).length
      }
    };
  }

  try {
    const response = await userService.getUsers(params);
    if (response.success && response.data?.users) {
      // Transform API data to match local User interface
      const transformedUsers: User[] = response.data.users.map(
        (apiUser: any) => ({
          id: apiUser.id,
          username: apiUser.username,
          email: apiUser.email,
          avatarUrl:apiUser.avatarUrl,
          firstName: apiUser.first_name || apiUser.firstName,
          lastName: apiUser.last_name || apiUser.lastName,
          role: apiUser.role,
          userType: apiUser.user_type || apiUser.userType,
          status: apiUser.is_active || apiUser.status,
          phone: apiUser.phone || "",
          branchId: apiUser.branch_id || apiUser.branchId,
          lastLogin: apiUser.last_login || apiUser.lastLogin,
          createdAt: apiUser.created_at || apiUser.createdAt,
          updatedAt: apiUser.updated_at || apiUser.updatedAt,
          skills: apiUser.skills || [],
          userSkills: apiUser.userSkills || [],
          image: apiUser.image || null,
          stages:apiUser.stages,
          userStages :apiUser.userStages
        })
      );
      
      return {
        users: transformedUsers,
        pagination: response.data.pagination || {
          page: DEFAULT_PAGINATION.page,
          limit: DEFAULT_PAGINATION.limit,
          pages: 1,
          total: transformedUsers.length,
        },
        counts: response.data.counts || {total:0, active: 0, inactive: 0 }
      };
    } else {
      return rejectWithValue("Failed to fetch users");
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch users"
    );
  }
});

export const fetchAllUsers = createAsyncThunk<
  User[],
  void,
  { rejectValue: string }
>("users/fetchAllUsers", async (_, { rejectWithValue }) => {
  if (USE_STUB_DATA) {
    // Return all stub users
    return STUB_USERS;
  }

  try {
    const response = await userService.getAllUsers();
    if (response.success && response.data) {
      // Transform API data to match local User interface
      const transformedUsers: User[] = response.data.map(
        (apiUser: any) => ({
          id: apiUser.id,
          username: apiUser.username,
          avatarUrl:apiUser.avatarUrl,
          email: apiUser.email,
          firstName: apiUser.first_name || apiUser.firstName,
          lastName: apiUser.last_name || apiUser.lastName,
          role: apiUser.role,
          userType: apiUser.user_type || apiUser.userType,
          status: apiUser.is_active || apiUser.status,
          phone: apiUser.phone || "",
          branchId: apiUser.branch_id || apiUser.branchId,
          lastLogin: apiUser.last_login || apiUser.lastLogin,
          createdAt: apiUser.created_at || apiUser.createdAt,
          updatedAt: apiUser.updated_at || apiUser.updatedAt,
          skills: apiUser.skills || [],
          userSkills: apiUser.userSkills || [],
          image: apiUser.image || null,
          stages: apiUser.stages,
          userStages: apiUser.userStages
        })
      );
      
      return transformedUsers;
    } else {
      return rejectWithValue("Failed to fetch all users");
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch all users"
    );
  }
});

export const fetchUserById = createAsyncThunk<
  User,
  string,
  { rejectValue: string }
>("users/fetchUserById", async (userId, { rejectWithValue }) => {
  if (USE_STUB_DATA) {
    const user = STUB_USERS.find((u) => u.id === userId);
    if (!user) throw new Error("User not found");
    return user;
  }

  try {
    const response = await userService.getUser(userId);
    console.log('API Response Debug:', { response, userId });
    if (response.success && response.data) {
      const apiUser = response.data as any;
      console.log('API User Data Debug:', apiUser.user);
      // Transform API data to match local User interface

      return apiUser.user;
    } else {
      return rejectWithValue("Failed to fetch user");
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch user"
    );
  }
});

export const createUser = createAsyncThunk<
  User,
  UserFormData,
  { rejectValue: string }
>("users/createUser", async (userData, { rejectWithValue }) => {


  try {
    const payload: any = {
      username: userData.username,
      email: userData.email,
      avatarUrl:userData.avatarUrl,
      password: userData.password , // Should be handled properly
      firstName: userData.firstName,
      role: userData.role,
      userType: userData.userType,
      status: userData.status,
      photo:userData.photo,
      phone: userData.phone,
      stages: userData.stages || [], // Send stages field as expected by backend
    };
    // Only include branchId if provided and non-empty; otherwise omit to imply global
    if (userData.branchId && userData.branchId.trim() !== "") {
      payload.branchId = userData.branchId;
    }
    
    console.log("🚀 Creating user with payload:", payload);
    console.log("🔍 Branch ID in payload:", payload.branchId);
    
    const response = await userService.createUser(payload);
    
    if (response.success) {
      console.log("response==>",response.data)
      
      // const apiUser = response.data;
      // console.log("apiUser====>",apiUser)
      // // Transform API data to match local User interface
      // const transformedUser: User = {
      //   id: apiUser.id,
      //   username: apiUser.username,
      //   email: apiUser.email,
      //   firstName: apiUser.first_name || apiUser.firstName,
      //   lastName: apiUser.last_name || apiUser.lastName,
      //   role: apiUser.role,
      //   userType: apiUser.user_type || apiUser.userType,
      //   status: apiUser.is_active || apiUser.status,
      //   phone: apiUser.phone || "",
      //   branchId: apiUser.branch_id || apiUser.branchId,
      //   lastLogin: apiUser.last_login || apiUser.lastLogin,
      //   createdAt: apiUser.created_at || apiUser.createdAt,
      //   updatedAt: apiUser.updated_at || apiUser.updatedAt,
      // };
      // return transformedUser;
      return (response.data as any)?.user || response.data
    } else {
      return rejectWithValue("Failed to create user");
    }
  } catch (error: any) {
    console.error("Create user error:", error);
    const errorMessage = error.response?.data?.error?.message || 
                        error.response?.data?.message || 
                        error.message || 
                        "Failed to create user";
    return rejectWithValue(errorMessage);
  }
});

export const updateUser = createAsyncThunk<
  User,
  { id: string; userData: Partial<UserFormData> },
  { rejectValue: string; state: RootState }
>(
  "users/updateUser",
  async ({ id, userData }, { rejectWithValue, getState, dispatch }) => {
    // Get current user to check if role changed
    const currentUser = getState().users.users.find(user => user.id === id);
    const roleChanged = currentUser && currentUser.role !== userData.role;

    try {
      // Prepare the data for backend update
      const updateData: any = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        avatarUrl:userData.avatarUrl,
        username:userData.username,
        email:userData.email,
        role: userData.role,
        userType: userData.userType,
        photo:userData.photo,
        status: userData.status,
        phone: userData.phone,
        stages: userData.stages,
      };
      if (userData.branchId !== undefined) {
        // Pass through provided branchId (can be empty to clear on backend if supported)
        updateData.branchId = userData.branchId;
      }

      const response = await userService.updateUser(id, updateData);
      if (response.success && response.data) {
        const apiUser = (response.data as any)?.user || response.data;
        
        // Transform the API response to match User interface
        const transformedUser: User = {
          id: apiUser.id || id,
          username: apiUser.username || "",
          avatarUrl:userData.avatarUrl || "",
          email: apiUser.email || "",
          firstName: apiUser.firstName || apiUser.first_name || "",
          lastName: apiUser.lastName || apiUser.last_name || "",
          role: apiUser.role || "worker",
          userType: apiUser.userType || apiUser.user_type || "regular",
          status: apiUser.status === "active" ? "active" : "inactive",
          phone: apiUser.phone || "",
          branchId: apiUser.branchId || apiUser.branch_id || "",
          lastLogin: apiUser.lastLogin || apiUser.last_login || "",
          createdAt: apiUser.createdAt || apiUser.created_at || new Date().toISOString(),
          updatedAt: apiUser.updatedAt || apiUser.updated_at || new Date().toISOString(),
          image: apiUser.image || null,
          stages: apiUser.stages || [],
          userStages: apiUser.userStages || [],
        };

        // If role changed, refresh user permissions
        if (roleChanged) {
          console.log('Role changed, refreshing user permissions for user:', id);
          // Import the auth slice action to refresh permissions
          const { refreshPermissions } = await import('../../store/slices/authSlice');
          dispatch(refreshPermissions());
        }
      
        return transformedUser;
      } else {
        return rejectWithValue("Failed to update user");
      }
    } catch (error: any) {
      console.error("Update user error:", error);
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          "Failed to update user";
      return rejectWithValue(errorMessage);
    }
  }
);

export const toggleUserStatus = createAsyncThunk<
  User,
  { id: string; status: string },
  { rejectValue: string }
>("users/toggleUserStatus", async ({ id, status }, { rejectWithValue }) => {
  if (USE_STUB_DATA) {
    return {
      ...STUB_USERS.find((u) => u.id === id)!,
      status,
      updatedAt: new Date().toISOString(),
    };
  }

  try {
    const response = await userService.updateUser(id, {status} );
    if (response.success && response.data) {
      const apiUser = response.data;
      
      // Handle different response structures
      let userData: any;
      if ((apiUser as any).user) {
        userData = (apiUser as any).user;
      } else if (apiUser.id) {
        userData = apiUser;
      } else {
        return rejectWithValue("Invalid user data received");
      }
      
      const transformedUser: User = {
        id: userData.id || id,
        username: userData.username || "",
        email: userData.email || "",
        avatarUrl:userData.avatarUrl || "",
        firstName: userData.firstName || userData.first_name || "",
        lastName: userData.lastName || userData.last_name || "",
        role: userData.role || "worker",
        userType: userData.userType || userData.user_type || "regular",
        status: userData.status === "active" ? "active" : "inactive", 
        phone: userData.phone || "",
        branchId: userData.branchId || userData.branch_id || "",
        lastLogin: userData.lastLogin || userData.last_login || "",
        createdAt: userData.createdAt || userData.created_at || new Date().toISOString(),
        updatedAt: userData.updatedAt || userData.updated_at || new Date().toISOString(),
        image: userData.image || null,
        skills: userData.skills || [],
        userSkills: userData.userSkills || [],
      };
      return transformedUser;
    } else {
      return rejectWithValue("Failed to update user status");
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to update user status"
    );
  }
});

// export const toggleUserStatus = createAsyncThunk(
//   "users/toggleUserStatus",
//   async ({ id, status }: { id: string; status: boolean }) => {
//     const response = await axios.patch(`${API_URL}/users/${id}/toggle-status`, {
//       status,
//     });
//     // ✅ Return only the updated user object
//     return response.data.data.user;
//   }
// );


export const deleteUser = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("users/deleteUser", async (userId, { rejectWithValue }) => {
  if (USE_STUB_DATA) {
    return userId;
  }

  try {
    const response = await userService.deleteUser(userId);
    if (response.success) {
      return userId;
    } else {
      return rejectWithValue("Failed to delete user");
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to delete user"
    );
  }
});



const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPagination: (state, action: PayloadAction<Partial<Pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    // fetchUsers
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users;
        state.pagination = action.payload.pagination;

        // Calculate counts from users array
        // const activeCount = action.payload.users.filter(user => user.status).length;
        // const inactiveCount = action.payload.users.filter(user => !user.status).length;
        state.counts = action.payload.counts;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Error toast is already handled by errorHandlingService in enhancedApiClient
      });

    // fetchAllUsers
    builder
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
        state.pagination = {
          page: 1,
          limit: action.payload.length,
          pages: 1,
          total: action.payload.length,
        };
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Error toast is already handled by errorHandlingService in enhancedApiClient
      });

    // fetchUserById
    builder
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Error toast is already handled by errorHandlingService in enhancedApiClient
      });

    // createUser
    builder
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        // Don't clear global error for individual operations
      })
      .addCase(createUser.fulfilled, (state, action) => {
        console.log("=====>action.paylod=> ", action.payload)
        console.log("*********action=> ", action)
        state.loading = false;
        state.users.unshift(action.payload)
        // state.users.push(action.payload);
        toast.success("User created successfully");
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        // Error toast is already handled by errorHandlingService in enhancedApiClient
      });

    // updateUser
    builder
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        // Don't clear global error for individual operations
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.users.findIndex((u) => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.selectedUser?.id === action.payload.id) {
          state.selectedUser = action.payload;
        }
        toast.success("User updated successfully...");
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        // Error toast is already handled by errorHandlingService in enhancedApiClient
      });

    // deleteUser
    builder
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        // Don't clear global error for individual operations
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter((u) => u.id !== action.payload);
        if (state.selectedUser?.id === action.payload) {
          state.selectedUser = null;
        }
        toast.success("User deleted successfully");
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        // Error toast is already handled by errorHandlingService in enhancedApiClient
      })

      //toggleUserStatus
      // .addCase(toggleUserStatus.fulfilled, (state, action) => {
        
      //   const index = state.users.findIndex((u) => u.id === action.payload.id);
      //   if (index !== -1) {
      //     state.users[index].status = action.payload.status;
      //     state.users[index].updatedAt = action.payload.updatedAt;
      //   }
      //   if (state.selectedUser?.id === action.payload.id) {
      //     state.selectedUser = {
      //       ...state.selectedUser,
      //       status: action.payload.status,
      //       updatedAt: action.payload.updatedAt,
      //     };
      //   }
      //   toast.success("User status updated");
      // })
      // .addCase(toggleUserStatus.rejected, (state, action) => {
      //   state.error = action.payload as string;
      //   toast.error(action.payload as string);
      // });

      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.users.findIndex((u) => u.id === updated.id);
        if (index !== -1) {
          state.users[index] = updated;
        }
        if (state.selectedUser?.id === updated.id) {
          state.selectedUser = updated;
        }
      if (state.counts) {
        if (updated.status === "active") {
          state.counts.active++;
          state.counts.inactive--;
        } else {
          state.counts.active--;
          state.counts.inactive++;
        }
      }
        toast.success("User status updated");
      })
      .addCase(toggleUserStatus.rejected, (state, action) => {
        // Error toast is already handled by errorHandlingService in enhancedApiClient
      });
      
      
  },
});

export const { setSelectedUser, clearError, setPagination } = userSlice.actions;

// Selectors
export const selectUsers = (state: RootState) => state.users.users;
export const selectActiveUsers = (state: RootState) =>
  state.users.users.filter((u: User) => u.status);
export const selectInactiveUsers = (state: RootState) =>
  state.users.users.filter((u: User) => !u.status);
export const selectUserLoading = (state: RootState) => state.users.loading;
export const selectUserError = (state: RootState) => state.users.error;
export const selectSelectedUser = (state: RootState) =>
  state.users.selectedUser;
export const selectUserPagination = (state: RootState) => state.users.pagination;
export const selectedUsersCount = (state: RootState) => state.users.counts;

export default userSlice.reducer;
